// frontend/src/pages/admin/FieldTaDaRatesPage.tsx
// TA/DA rate card — Phase 5 (2026-07-29), spec §6.5. First client for
// GET/PUT /field-ta-da/rates, which shipped in Phase 3 with none.
//
// 🔒 RATES DEFAULT TO ZERO AND OVER-CLAIM IS AN ADMIN SWITCH (Manak, spec §6.5).
// With an empty card the API returns ONE implicit row — 0/0, over-claim ON —
// and that is the real, intended shipped state, not an empty table. This screen
// renders it as such and explains it, because zeros with no explanation read as
// a broken feature and the first instinct is to "fix" them.
//
// 🔒 Money is PAISE on the wire. Rupees exist only in the <input> and the
// rendered string; toPaise() is the single boundary and the API rejects a
// non-integer outright.
//
// 🔒 AUTHORIZATION (SUN-42, 2026-10-18) — one pair, everywhere:
//     READ  (sidebar, this page, GET /field-ta-da/rates)
//           → manage_ta_da_rates OR approve_ta_da  (TA_DA_RATES_READ_PERMISSIONS)
//     WRITE (PUT /field-ta-da/rates, the save controls via the server's own
//           `canEdit`) → manage_ta_da_rates  (admin/owner/super_admin by default)
// An approver sees the rules claims are judged by but cannot change them.
//
// 🔒 The default (all grades) row is always active — the server refuses to
// deactivate it, so it has no Active switch here.
//
// Rendered and pinned by FieldTaDaRatesPage.sun42.test.tsx.
//
// Spec: docs/superpowers/specs/2026-07-29-field-activity-and-sessions-design.md

import { useCallback, useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { useMyPermissions } from '../../hooks/useMyPermissions';
import { Empty, Section, TD, TH, paiseToInput, rupees, toPaise } from '../crm/fieldFormat';
import { TA_DA_RATES_READ_PERMISSIONS } from '../../components/layout/sidebarRegistry';

/**
 * One rupee field → paise, or the reason it cannot be saved.
 * Blank is 0 here (a rate card with no per-km rate is a per-km rate of zero).
 * Anything that is not a plain non-negative amount with at most 2 decimals is
 * REFUSED, never turned into 0 and never rounded (₹1.234 is not ₹1.23).
 */
export function parseRateField(
  input: string,
  name: string,
): { paise: number; error: null } | { paise: null; error: string } {
  const s = (input ?? '').trim();
  if (s === '') return { paise: 0, error: null };
  if (/^-/.test(s)) return { paise: null, error: `${name}: an amount cannot be negative.` };
  if (/^\d+\.\d{3,}$/.test(s)) {
    return { paise: null, error: `${name}: use at most 2 decimal places (paise).` };
  }
  if (!/^(\d+(\.\d{0,2})?|\.\d{1,2})$/.test(s)) {
    return { paise: null, error: `${name}: enter an amount in rupees, like 12 or 12.50.` };
  }
  const paise = toPaise(s);
  if (paise === null || !Number.isSafeInteger(paise)) {
    return { paise: null, error: `${name}: enter an amount in rupees, like 12 or 12.50.` };
  }
  return { paise, error: null };
}

type RateRow = {
  id: string | null;
  gradeId: string | null;
  gradeName: string | null;
  perKmPaise: number;
  perDayPaise: number;
  allowOverClaim: boolean;
  active: boolean;
  /** True for the synthetic 0/0/over-claim-ON row the API returns when the
   *  tenant has no rows at all. Saving it is what creates the real row. */
  implicit: boolean;
};

type Grade = { id: string; name: string };

/** What the form holds while being edited — rupee strings, not paise. */
type Draft = {
  gradeId: string | null;
  perKm: string;
  perDay: string;
  allowOverClaim: boolean;
  active: boolean;
};

const draftFrom = (r: RateRow): Draft => ({
  gradeId: r.gradeId,
  perKm: paiseToInput(r.perKmPaise),
  perDay: paiseToInput(r.perDayPaise),
  allowOverClaim: r.allowOverClaim,
  active: r.active,
});

const rowKey = (r: RateRow): string => r.gradeId ?? '__default__';

const gradeLabel = (r: { gradeId: string | null; gradeName: string | null }): string =>
  r.gradeId ? (r.gradeName ?? 'Unnamed grade') : 'All grades — default';

export function FieldTaDaRatesPage() {
  const { permissions, loading: permsLoading } = useMyPermissions();
  const mayView = TA_DA_RATES_READ_PERMISSIONS.some((k) => permissions[k] === true);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [rates, setRates] = useState<RateRow[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  // 🔒 The server's own answer for manage_ta_da_rates, not a second guess at it.
  const [canEdit, setCanEdit] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/field-ta-da/rates');
      const data = res.data?.data ?? {};
      setRates(Array.isArray(data.rates) ? data.rates : []);
      setGrades(Array.isArray(data.grades) ? data.grades : []);
      setCanEdit(data.canEdit === true);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not load the travel rate card');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mayView) load();
    else setLoading(false);
  }, [mayView, load]);

  const startEdit = (r: RateRow) => {
    setNotice(null);
    setError(null);
    setEditingKey(rowKey(r));
    setDraft(draftFrom(r));
  };

  const startAdd = (gradeId: string) => {
    setNotice(null);
    setError(null);
    setEditingKey(gradeId);
    setDraft({ gradeId, perKm: '', perDay: '', allowOverClaim: true, active: true });
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraft(null);
  };

  const save = useCallback(async () => {
    if (!draft) return;
    // 🔒 Blank means zero HERE (unlike the target form, where blank means
    // "not measured"): a rate card with no per-km rate is a per-km rate of
    // zero, which is exactly the shipped default. A BAD amount ('abc', '-5')
    // is not blank — it is refused with a message, never saved as ₹0.
    const km = parseRateField(draft.perKm, 'Per km');
    const day = parseRateField(draft.perDay, 'Per day');
    if (km.error !== null || day.error !== null) {
      setNotice(null);
      setError([km.error, day.error].filter(Boolean).join(' '));
      return;
    }
    const perKmPaise = km.paise;
    const perDayPaise = day.paise;

    // 🔒 The freeze-claims trap. With over-claim off and both rates zero the
    // entitlement is ₹0.00, and the backend refuses every claim above it with
    // a long explanation. Stop the admin walking into that rather than letting
    // reps discover it on the road.
    if (!draft.allowOverClaim && perKmPaise === 0 && perDayPaise === 0) {
      const ok = window.confirm(
        'With no rates set, turning this off means nobody can claim anything above ₹0.00. Continue?',
      );
      if (!ok) return;
    }

    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await api.put('/field-ta-da/rates', {
        gradeId: draft.gradeId,
        perKmPaise,
        perDayPaise,
        allowOverClaim: draft.allowOverClaim,
        // The default row is always active (server rule, SUN-42).
        active: draft.gradeId === null ? true : draft.active,
      });
      cancelEdit();
      setNotice('Saved.');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not save the rate');
    } finally {
      setSaving(false);
    }
  }, [draft, load]);

  if (permsLoading) {
    return <div style={{ padding: 24 }}><p style={{ color: '#6b7280' }}>Loading…</p></div>;
  }

  // 🔒 Fails closed — see useMyPermissions. A user who genuinely holds the
  // permission but hit a network blip sees this and gets it back on reload;
  // the alternative is offering a page that 403s.
  if (!mayView) {
    return (
      <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Travel rates</h1>
        <Empty>You do not have access to travel rates.</Empty>
      </div>
    );
  }

  const usedGradeIds = new Set(rates.filter((r) => r.gradeId).map((r) => r.gradeId as string));
  const addableGrades = grades.filter((g) => !usedGradeIds.has(g.id));
  const hasImplicitOnly = rates.length === 1 && rates[0]?.implicit === true;

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Travel rates (TA/DA)</h1>

      {/* 🔒 Permanent, not dismissible. Without it a card of zeros reads as a
          broken feature and the first instinct is to "fix" it. */}
      <div
        style={{
          marginTop: 12, padding: 14, borderRadius: 10,
          background: '#FFF8E7', border: '1px solid #F0DCA8', color: '#6B4E12', fontSize: 13,
        }}
      >
        Travel rates start at zero. While <strong>allow claims above entitlement</strong> is on, the
        calculated amount is only a reference — your reps enter what they are actually claiming, and
        you approve it. Set rates here when you want the system to calculate the amount for them.
      </div>

      {hasImplicitOnly && (
        <p style={{ fontSize: 13, color: '#6b7280', marginTop: 12 }}>
          Nothing has been set yet, so the row below is the state the system is currently in — not a
          blank form. Save it to make it a real rate card.
        </p>
      )}

      {error && (
        <div style={{ padding: 12, background: '#fef2f2', color: '#b91c1c', borderRadius: 8, marginTop: 16 }}>
          {error}
        </div>
      )}
      {notice && (
        <div style={{ padding: 12, background: '#EAF6EF', color: '#2C6D4A', borderRadius: 8, marginTop: 16 }}>
          {notice}
        </div>
      )}

      {loading && <p style={{ color: '#6b7280' }}>Loading…</p>}

      {!loading && (
        <Section title="Rate card">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#6b7280', fontSize: 12 }}>
                <th style={TH}>Grade</th>
                <th style={TH}>Per km</th>
                <th style={TH}>Per day</th>
                <th style={TH}>Claims above entitlement</th>
                <th style={TH} />
              </tr>
            </thead>
            <tbody>
              {rates.map((r) => {
                const key = rowKey(r);
                const isEditing = editingKey === key && draft != null;
                return (
                  <tr key={key} style={{ borderTop: '1px solid #f3f4f6', verticalAlign: 'top' }}>
                    <td style={TD}>
                      <div style={{ fontWeight: 600 }}>{gradeLabel(r)}</div>
                      {r.implicit && (
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>not saved yet</div>
                      )}
                      {!r.active && (
                        <div style={{ fontSize: 11, color: '#B55A00' }}>inactive — ignored when claiming</div>
                      )}
                    </td>

                    {isEditing ? (
                      <>
                        <td style={TD}>
                          <RupeeInput
                            value={draft!.perKm}
                            onChange={(v) => setDraft({ ...draft!, perKm: v })}
                            label="rupees per km"
                          />
                        </td>
                        <td style={TD}>
                          <RupeeInput
                            value={draft!.perDay}
                            onChange={(v) => setDraft({ ...draft!, perDay: v })}
                            label="rupees per day out"
                          />
                        </td>
                        <td style={{ ...TD, maxWidth: 300 }}>
                          <OverClaimSwitch
                            value={draft!.allowOverClaim}
                            onChange={(v) => setDraft({ ...draft!, allowOverClaim: v })}
                          />
                          {/* The default row has no Active switch: the server
                              refuses to deactivate it (reps without a grade
                              rate would lose their cap). */}
                          {r.gradeId !== null && (
                            <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                              <input
                                type="checkbox"
                                checked={draft!.active}
                                onChange={(e) => setDraft({ ...draft!, active: e.target.checked })}
                              />
                              Active
                            </label>
                          )}
                        </td>
                        <td style={TD}>
                          <button onClick={save} disabled={saving} style={BTN_PRIMARY}>
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                          <button onClick={cancelEdit} disabled={saving} style={BTN_PLAIN}>
                            Cancel
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={TD}>{rupees(r.perKmPaise)}</td>
                        <td style={TD}>{rupees(r.perDayPaise)}</td>
                        <td style={TD}>
                          {r.allowOverClaim ? (
                            <span style={{ color: '#2C6D4A', fontWeight: 600 }}>Allowed</span>
                          ) : (
                            <span style={{ color: '#A8322B', fontWeight: 600 }}>Capped at entitlement</span>
                          )}
                        </td>
                        <td style={TD}>
                          {canEdit ? (
                            <button onClick={() => startEdit(r)} style={BTN_PLAIN}>Edit</button>
                          ) : (
                            <span style={{ fontSize: 12, color: '#9ca3af' }}>view only</span>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {!canEdit && (
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 10 }}>
              You can see the rate card but not change it. Setting rates needs the same authority as
              approving claims against them.
            </p>
          )}
        </Section>
      )}

      {!loading && canEdit && addableGrades.length > 0 && (
        <Section title="Add a rate for a grade">
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>
            A grade with its own row uses that row; everyone else falls back to the default above.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {addableGrades.map((g) => (
              <button key={g.id} onClick={() => startAdd(g.id)} style={BTN_PLAIN}>
                + {g.name}
              </button>
            ))}
          </div>
          {editingKey && draft?.gradeId && !usedGradeIds.has(draft.gradeId) && (
            <div style={{ marginTop: 14, padding: 14, border: '1px solid #e5e7eb', borderRadius: 10 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                {grades.find((g) => g.id === draft.gradeId)?.name ?? 'New grade rate'}
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <RupeeInput
                  value={draft.perKm}
                  onChange={(v) => setDraft({ ...draft, perKm: v })}
                  label="rupees per km"
                />
                <RupeeInput
                  value={draft.perDay}
                  onChange={(v) => setDraft({ ...draft, perDay: v })}
                  label="rupees per day out"
                />
              </div>
              <div style={{ marginTop: 10, maxWidth: 420 }}>
                <OverClaimSwitch
                  value={draft.allowOverClaim}
                  onChange={(v) => setDraft({ ...draft, allowOverClaim: v })}
                />
              </div>
              <div style={{ marginTop: 10 }}>
                <button onClick={save} disabled={saving} style={BTN_PRIMARY}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={cancelEdit} disabled={saving} style={BTN_PLAIN}>Cancel</button>
              </div>
            </div>
          )}
        </Section>
      )}

      {!loading && grades.length === 0 && (
        <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 16 }}>
          No employee grades are set up, so the default row applies to everyone. That is a normal
          configuration, not a missing step.
        </p>
      )}
    </div>
  );
}

/**
 * A rupee amount. `inputMode="decimal"` rather than `type="number"`, so the
 * value stays an exact string until toPaise() converts it — a number input
 * hands back a float and invites a rounding argument later.
 */
function RupeeInput({
  value, onChange, label,
}: { value: string; onChange: (v: string) => void; label: string }) {
  return (
    <label style={{ display: 'block', fontSize: 12, color: '#6b7280' }}>
      {label}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
        <span style={{ color: '#6b7280' }}>₹</span>
        <input
          inputMode="decimal"
          value={value}
          placeholder="0.00"
          onChange={(e) => onChange(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, width: 110 }}
        />
      </div>
    </label>
  );
}

/**
 * 🔒 Not a bare toggle. The consequence is spelled out in both positions,
 * because "allow over-claim" tells an admin nothing about what their reps will
 * be able to do tomorrow morning.
 */
function OverClaimSwitch({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
          style={{ marginTop: 3 }}
        />
        <span style={{ fontSize: 13 }}>
          <strong>Allow claims above entitlement</strong>
          <span style={{ display: 'block', color: '#6b7280', fontSize: 12, marginTop: 2 }}>
            {value
              ? 'Reps may claim more than the calculated amount. You approve each claim.'
              : 'Reps may claim only up to the calculated amount.'}
          </span>
        </span>
      </label>
    </div>
  );
}

const BTN_PRIMARY = {
  padding: '6px 12px', marginRight: 6, borderRadius: 6, cursor: 'pointer',
  border: '1px solid #3AA06B', background: '#EAF6EF', color: '#2C6D4A', fontWeight: 600,
};
const BTN_PLAIN = {
  padding: '6px 12px', marginRight: 6, borderRadius: 6, cursor: 'pointer',
  border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontWeight: 600,
};
